/**
 * Sustainability Analytics Platform
 * Comprehensive carbon footprint tracking and environmental impact visualization
 * Based on HIVED's 76% emission reduction patterns and Decade Energy's renewable integration
 */

import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Leaf,
  Zap,
  TreePine,
  Gauge,
  TrendingUp,
  TrendingDown,
  Calculator,
  Award,
  Target,
  Globe,
  Battery,
  Fuel,
  Factory,
  Wind,
  Recycle,
  Calendar,
} from 'lucide-react';

// Enhanced UI Components
import { BentoGrid, BentoGridItem } from '../common/BentoGrid';
import { MagicCard } from '../ui/magic/MagicCard';
import { NumberTicker } from '../ui/magic/NumberTicker';
import { AnimatedCircularProgressBar } from '../ui/magic/AnimatedCircularProgressBar';
import { cn } from '../../lib/utils';

// Context and hooks
import { useProject } from '../../contexts/ProjectContext';
import { useVehicles } from '../../hooks/useVehicles';
import { useProjects } from '../../hooks/useProjects';

// Types
interface SustainabilityMetrics {
  carbonFootprint: number; // kg CO2 per year
  carbonReduction: number; // percentage reduction
  renewableEnergy: number; // percentage of energy from renewable sources
  fuelSavings: number; // liters saved per year
  costSavings: number; // USD saved per year
  electricVehicleRatio: number; // percentage of electric vehicles
  routeOptimizationSavings: number; // percentage savings from optimization
  environmentalScore: number; // overall score (0-100)
}

interface CO2Calculator {
  distance: number; // km
  vehicleType: 'electric' | 'hybrid' | 'diesel' | 'gasoline';
  load: number; // percentage of capacity
}

interface SustainabilityDashboardProps {
  className?: string;
  compactMode?: boolean;
  showCalculator?: boolean;
  timeframe?: '7d' | '30d' | '90d' | '1y';
}

// CO2 emission factors (kg CO2 per km)
const EMISSION_FACTORS = {
  electric: 0.0, // Assuming renewable energy
  hybrid: 0.12,
  diesel: 0.27,
  gasoline: 0.23,
};

// HIVED benchmark: 76% emission reduction potential
const HIVED_BENCHMARK = 76;

// Decade Energy renewable integration target
const RENEWABLE_TARGET = 85;

export const SustainabilityDashboard: React.FC<SustainabilityDashboardProps> = ({
  className,
  compactMode = false,
  showCalculator = true,
  timeframe = '30d',
}) => {
  const { currentProject } = useProject();
  const { data: projects } = useProjects();
  const { data: vehicles = [], isLoading, error } = useVehicles(currentProject?.id || 0);
  
  const [calculatorValues, setCalculatorValues] = useState<CO2Calculator>({
    distance: 100,
    vehicleType: 'diesel',
    load: 75,
  });

  // Calculate sustainability metrics based on vehicle data
  const sustainabilityMetrics = useMemo((): SustainabilityMetrics => {
    if (!vehicles.length) {
      return {
        carbonFootprint: 0,
        carbonReduction: 0,
        renewableEnergy: 0,
        fuelSavings: 0,
        costSavings: 0,
        electricVehicleRatio: 0,
        routeOptimizationSavings: 0,
        environmentalScore: 0,
      };
    }

    // Analyze vehicle composition
    const electricVehicles = vehicles.filter(v => 
      v.description?.toLowerCase().includes('electric') || 
      v.description?.toLowerCase().includes('ev')
    ).length;
    
    const hybridVehicles = vehicles.filter(v => 
      v.description?.toLowerCase().includes('hybrid')
    ).length;
    
    const totalVehicles = vehicles.length;
    const electricRatio = (electricVehicles / totalVehicles) * 100;
    const hybridRatio = (hybridVehicles / totalVehicles) * 100;
    
    // Calculate emissions based on HIVED research patterns
    const baseEmissions = totalVehicles * 12000; // kg CO2 per vehicle per year
    const electricSavings = electricVehicles * 12000 * 0.95; // 95% reduction for electric
    const hybridSavings = hybridVehicles * 12000 * 0.4; // 40% reduction for hybrid
    const routeOptimizationSavings = baseEmissions * 0.15; // 15% from route optimization
    
    const totalSavings = electricSavings + hybridSavings + routeOptimizationSavings;
    const carbonReduction = Math.min((totalSavings / baseEmissions) * 100, HIVED_BENCHMARK);
    
    // Calculate renewable energy usage (based on electric vehicle ratio and grid mix)
    const renewableEnergy = Math.min(electricRatio * 0.8 + 20, RENEWABLE_TARGET); // 80% of electric + 20% baseline
    
    // Calculate fuel savings (liters per year)
    const fuelSavings = (electricVehicles * 2400) + (hybridVehicles * 960); // liters saved per year
    
    // Calculate cost savings (fuel + maintenance)
    const costSavings = (fuelSavings * 1.5) + (totalVehicles * 500); // USD per year
    
    // Environmental score (0-100) based on multiple factors
    const environmentalScore = Math.round(
      (carbonReduction * 0.4) + 
      (renewableEnergy * 0.3) + 
      (electricRatio * 0.2) + 
      (15 * 0.1) // Route optimization baseline
    );

    return {
      carbonFootprint: Math.round(baseEmissions - totalSavings),
      carbonReduction: Math.round(carbonReduction),
      renewableEnergy: Math.round(renewableEnergy),
      fuelSavings: Math.round(fuelSavings),
      costSavings: Math.round(costSavings),
      electricVehicleRatio: Math.round(electricRatio),
      routeOptimizationSavings: 15,
      environmentalScore,
    };
  }, [vehicles]);

  // Calculate CO2 for custom inputs
  const calculateCO2 = (calc: CO2Calculator): number => {
    const baseCO2 = calc.distance * EMISSION_FACTORS[calc.vehicleType];
    const loadFactor = 1 + (calc.load / 100) * 0.2; // 20% increase at full load
    return baseCO2 * loadFactor;
  };

  // Loading state
  if (isLoading) {
    return (
      <MagicCard className="p-8">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Loading Sustainability Data</h3>
            <p className="text-sm text-gray-600">Calculating environmental impact...</p>
          </div>
        </div>
      </MagicCard>
    );
  }

  // Error state
  if (error) {
    return (
      <MagicCard className="p-8 border-red-200 bg-red-50">
        <div className="text-center space-y-4">
          <Factory className="w-12 h-12 text-red-600 mx-auto" />
          <div>
            <h3 className="text-lg font-semibold text-red-800">Unable to Load Environmental Data</h3>
            <p className="text-sm text-red-600 mt-1">
              {error instanceof Error ? error.message : 'Unknown error occurred'}
            </p>
          </div>
        </div>
      </MagicCard>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-green-100">
            <Leaf className="w-8 h-8 text-green-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Sustainability Analytics</h2>
            <p className="text-gray-600">
              Environmental impact tracking for {currentProject?.name || 'All Projects'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <motion.div
            className="flex items-center space-x-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <TreePine className="w-4 h-4" />
            <span>Carbon Neutral Target</span>
          </motion.div>
        </div>
      </div>

      {/* Main Metrics Grid */}
      <BentoGrid columns={compactMode ? { sm: 2, md: 3, lg: 4 } : { sm: 1, md: 2, lg: 4 }} gap={6}>
        {/* Carbon Footprint */}
        <BentoGridItem priority="high">
          <MagicCard className="p-6 h-full bg-gradient-to-br from-red-50 to-orange-50">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Factory className="w-5 h-5 text-red-600" />
                <h3 className="text-sm font-medium text-gray-700">Carbon Footprint</h3>
              </div>
              <div className="text-xs text-gray-500">{timeframe}</div>
            </div>
            
            <div className="space-y-2">
              <NumberTicker
                value={sustainabilityMetrics.carbonFootprint}
                suffix=" kg CO₂"
                className="text-2xl font-bold text-red-600"
                duration={2.0}
              />
              <p className="text-xs text-gray-600">Annual emissions</p>
            </div>
          </MagicCard>
        </BentoGridItem>

        {/* Carbon Reduction */}
        <BentoGridItem priority="high">
          <MagicCard className="p-6 h-full bg-gradient-to-br from-green-50 to-emerald-50">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <TrendingDown className="w-5 h-5 text-green-600" />
                <h3 className="text-sm font-medium text-gray-700">CO₂ Reduction</h3>
              </div>
              {sustainabilityMetrics.carbonReduction >= HIVED_BENCHMARK * 0.8 && (
                <Award className="w-4 h-4 text-yellow-500" />
              )}
            </div>
            
            <div className="space-y-2">
              <div className="flex items-baseline space-x-2">
                <NumberTicker
                  value={sustainabilityMetrics.carbonReduction}
                  suffix="%"
                  className="text-2xl font-bold text-green-600"
                  duration={2.2}
                />
                <span className="text-xs text-gray-500">/ {HIVED_BENCHMARK}% target</span>
              </div>
              
              <AnimatedCircularProgressBar
                max={HIVED_BENCHMARK}
                min={0}
                value={sustainabilityMetrics.carbonReduction}
                gaugePrimaryColor="rgb(34 197 94)"
                gaugeSecondaryColor="rgba(34 197 94 0.2)"
                className="h-12"
              />
            </div>
          </MagicCard>
        </BentoGridItem>

        {/* Renewable Energy */}
        <BentoGridItem priority="medium">
          <MagicCard className="p-6 h-full bg-gradient-to-br from-blue-50 to-cyan-50">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Wind className="w-5 h-5 text-blue-600" />
                <h3 className="text-sm font-medium text-gray-700">Renewable Energy</h3>
              </div>
              <Zap className="w-4 h-4 text-blue-500" />
            </div>
            
            <div className="space-y-2">
              <NumberTicker
                value={sustainabilityMetrics.renewableEnergy}
                suffix="%"
                className="text-2xl font-bold text-blue-600"
                duration={2.4}
              />
              <p className="text-xs text-gray-600">Clean energy usage</p>
            </div>
          </MagicCard>
        </BentoGridItem>

        {/* Environmental Score */}
        <BentoGridItem priority="medium">
          <MagicCard className="p-6 h-full bg-gradient-to-br from-purple-50 to-indigo-50">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Globe className="w-5 h-5 text-purple-600" />
                <h3 className="text-sm font-medium text-gray-700">Environmental Score</h3>
              </div>
              <Target className="w-4 h-4 text-purple-500" />
            </div>
            
            <div className="space-y-2">
              <NumberTicker
                value={sustainabilityMetrics.environmentalScore}
                suffix="/100"
                className="text-2xl font-bold text-purple-600"
                duration={2.6}
              />
              
              <AnimatedCircularProgressBar
                max={100}
                min={0}
                value={sustainabilityMetrics.environmentalScore}
                gaugePrimaryColor="rgb(147 51 234)"
                gaugeSecondaryColor="rgba(147 51 234 0.2)"
                className="h-12"
              />
            </div>
          </MagicCard>
        </BentoGridItem>
      </BentoGrid>

      {/* Secondary Metrics */}
      <BentoGrid columns={{ sm: 1, md: 2, lg: 3 }} gap={4}>
        {/* Fuel Savings */}
        <BentoGridItem>
          <MagicCard className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Fuel className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium text-gray-700">Fuel Savings</span>
              </div>
              <NumberTicker
                value={sustainabilityMetrics.fuelSavings}
                suffix=" L/year"
                className="text-lg font-semibold text-amber-600"
                duration={1.8}
              />
            </div>
          </MagicCard>
        </BentoGridItem>

        {/* Cost Savings */}
        <BentoGridItem>
          <MagicCard className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <TrendingUp className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-gray-700">Cost Savings</span>
              </div>
              <NumberTicker
                value={sustainabilityMetrics.costSavings}
                prefix="$"
                suffix="/year"
                className="text-lg font-semibold text-green-600"
                duration={2.0}
              />
            </div>
          </MagicCard>
        </BentoGridItem>

        {/* Electric Vehicle Ratio */}
        <BentoGridItem>
          <MagicCard className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Battery className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">Electric Vehicles</span>
              </div>
              <NumberTicker
                value={sustainabilityMetrics.electricVehicleRatio}
                suffix="%"
                className="text-lg font-semibold text-blue-600"
                duration={1.6}
              />
            </div>
          </MagicCard>
        </BentoGridItem>
      </BentoGrid>

      {/* CO2 Calculator */}
      {showCalculator && (
        <MagicCard className="p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Calculator className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">CO₂ Calculator</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Distance (km)
              </label>
              <input
                type="number"
                value={calculatorValues.distance}
                onChange={(e) => setCalculatorValues(prev => ({ 
                  ...prev, 
                  distance: Number(e.target.value) 
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vehicle Type
              </label>
              <select
                value={calculatorValues.vehicleType}
                onChange={(e) => setCalculatorValues(prev => ({ 
                  ...prev, 
                  vehicleType: e.target.value as CO2Calculator['vehicleType']
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="electric">Electric</option>
                <option value="hybrid">Hybrid</option>
                <option value="diesel">Diesel</option>
                <option value="gasoline">Gasoline</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Load (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={calculatorValues.load}
                onChange={(e) => setCalculatorValues(prev => ({ 
                  ...prev, 
                  load: Number(e.target.value) 
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="flex items-end">
              <div className="w-full p-3 bg-gray-50 rounded-lg border">
                <div className="text-sm text-gray-600">CO₂ Emissions</div>
                <div className="text-lg font-semibold text-gray-900">
                  {calculateCO2(calculatorValues).toFixed(2)} kg
                </div>
              </div>
            </div>
          </div>
        </MagicCard>
      )}

      {/* Research Integration Footer */}
      <MagicCard className="p-4 bg-gradient-to-r from-green-50 to-blue-50">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Award className="w-4 h-4 text-green-600" />
              <span>HIVED Research: 76% reduction target</span>
            </div>
            <div className="flex items-center space-x-1">
              <Wind className="w-4 h-4 text-blue-600" />
              <span>Decade Energy: {RENEWABLE_TARGET}% renewable target</span>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <Calendar className="w-4 h-4" />
            <span>Updated: {new Date().toLocaleDateString()}</span>
          </div>
        </div>
      </MagicCard>
    </div>
  );
};